import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ColumnStore, GridColumnConfig, ColumnConfig, Model, CellTooltip, MenuItemConfig, Menu, Tooltip, StringHelper } from '@bryntum/grid';
import { BryntumGridModule, BryntumGridComponent, BryntumGridProps } from '@bryntum/grid-angular';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { bootstrapArrowsAngleContract, bootstrapArrowsAngleExpand } from '@ng-icons/bootstrap-icons';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { Observable, take } from 'rxjs';
import writeXlsxFile, { Cell } from 'write-excel-file/browser';
import { ContainerService } from '../../../../../shared/Container/container.service';
import { selectUserType } from '../../../../../shared/app-configuration/app-configuration.reducer';
import { UserType } from '../../../../../shared/app-configuration/models/UserType';
import { BlobStorageService } from '../../../../../shared/blob-storage/blob-storage.service';
import { BryntumGridStateProvider } from '../../../../../shared/bryntum/bryntum-grid-state-provider';
import { BryntumGridStateService } from '../../../../../shared/bryntum/bryntum-grid-state.service';
import { ProofOfDeliveryPdfService } from '../../../../../shared/proof-of-delivery/proof-of-delivery-pdf.service';
import { IRootState } from '../../../../app.config';
import { IContainerSearchState, selectContainerSearch } from '../../../../../shared/Container/container.reducer';
import StatusFlags from '../../../../../shared/Container/enum/StatusFlags';
import { HttpErrorResponse } from '@angular/common/http';
import { ContainerStatusHelper } from '../../../../../shared/Container/Helper/ContainerStatusHelper';
import { ContainerStatusColumnConfig } from '../../../../../shared/Container/constant/ContainerStatusColumnConfig';
import { ContainerStatusConstant } from '../../../../../shared/Container/constant/ContainerStatusConstant';
import ContainerUpdateStatusRequest from '../../../../../shared/Container/model/ContainerUpdateStatusRequest';
import { ContainerChangeStatusModalComponent } from '../container-change-status-modal/container-change-status-modal.component';
import { ConfirmationService } from '../../../../../shared/modals/confirmation-modal/confirmation.service';
import { ToasterService } from '../../../../../shared/toaster/toaster.service';
import { ContainerDisplayStatus } from '../../../../../shared/Container/model/ContainerDisplayStatus';
import { getBooleanColumnGenericConfig, getNumberColumnGenericConfig, getTimeColumnGenericConfig } from '../../../../../shared/bryntum/Helper/MogoBryntumGridConfigHelper';
import { ContainerJobPreviewPodModalComponent } from '../../../../../shared/modals/container-job-preview-pod-modal/container-job-preview-pod-modal.component';
import { ContainerJobEyeViewModalComponent } from '../../../../../shared/modals/container-job-eye-view-modal/container-job-eye-view-modal.component';
import ContainerJobGridStatusMovement from '../../../../../shared/Container/model/ContainerJobGridStatusMovement';
import { DateTimeHelper } from '../../../../../shared/DateTime/DateTimeHelper';
import { ContainerMovementListItem } from '../../../../../shared/Container/model/ContainerMovementListItem';
import { ContainerUpdateModalComponent } from '../../../../../shared/modals/container-update-modal/container-update-modal.component';
import { ContainerJobItemStatusEnum } from '../../../../../shared/Container/enum/ContainerJobItemStatusEnum';
import ContainerUpdateRequest from '../../../../../shared/Container/model/ContainerUpdateRequest';


@Component({
  selector: 'app-container-job-item-status',
  standalone: true,
  imports: [BryntumGridModule, NgIcon],
  templateUrl: './container-job-item-status.component.html',
  styleUrl: './container-job-item-status.component.scss',
  viewProviders: [
    provideIcons({
      bootstrapArrowsAngleContract,
      bootstrapArrowsAngleExpand,
    })
  ]
})

export class ContainerJobItemStatusComponent implements OnInit, OnDestroy {
  data: ContainerJobGridStatusMovement[] = [];
  totalItems = 0;
  isInitializing = true;
  isLoadingJobs = false;
  gridSelected: ContainerJobGridStatusMovement[] = [];
  selectedTotalItems = 0;
  gridHeight: string = '79vh';
  statusFlags: StatusFlags = {
    isReadyForPickup: false,
    isPack: false,
    isUnpack: false,
    none: true
  };
  searchDateRange: IContainerSearchState['searchParams'] | null = null;
  private pendingSearchDateRange: IContainerSearchState['searchParams'] | null = null;
  containerGridStateId = 'containerItemGrid';
  readonly maxContainerUpdateItems = 25;
  private _containerGrid: BryntumGridComponent | null = null;
  @ViewChild('containerGrid')
  set containerGrid(value: BryntumGridComponent | null) {
    this._containerGrid = value;
    this.syncGridMask();
    this.updateTotalItemsFromStore();
  }
  get containerGrid(): BryntumGridComponent | null {
    return this._containerGrid;
  }
  userType: UserType | null = null;
  containerLocationTypeThatDisplayEmptyPark: string[] = ['Container park', 'MT park'];

  private gridStateProvider: BryntumGridStateProvider = new BryntumGridStateProvider(this.bryntumGridStateService, 33, this.containerGridStateId);

  constructor(private router: Router,
    private readonly store$: Store<IRootState>,
    private readonly modalService: NgbModal,
    private readonly confirmationService: ConfirmationService,
    private readonly proofOfDeliveryPdfService: ProofOfDeliveryPdfService,
    private readonly blobStorageService: BlobStorageService,
    private readonly containerService: ContainerService,
    private readonly bryntumGridStateService: BryntumGridStateService,
    private readonly toasterService: ToasterService
  ) { }

  gridProps: BryntumGridProps | null = null;

  isFreightUser = (): boolean => this.userType === UserType.FreightPayer;

  ngOnDestroy(): void {
    if (this.gridStateProvider) {
      this.gridStateProvider.destroy();
    }
  }

  ngOnInit() {
    this.isInitializing = true;
    this.data = [];

    this.gridStateProvider
    .init()
    .then(() => this.initialiseGrid());

    this.store$.select(selectContainerSearch).subscribe(result => {
      const fromDayJs = dayjs(result.from).add(1, 'day');
      const toDayJs = dayjs(result.to).add(1, 'day');
      this.searchDateRange = {
        from: new Date(fromDayJs.toString()),
        to: new Date(toDayJs.toString()),
        searchDateOption: result.searchDateOption
      };

      this.loadJobsList(this.searchDateRange);
    });
  }

  private initialiseGrid() {
    this.store$.select(selectUserType).subscribe((userType) => {
      this.userType = userType;

      let columns: (GridColumnConfig & { field: string, excelFormat?: string | ((value: number) => string) })[] = [
        {
          id: 'job_date',
          field: 'jobDateLocal',
          text: 'Job Date',
          autoWidth: 1,
          type: 'date',
          format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.jobDateLocal && record.jobDateLocal.toString() !== 'Invalid Date' ? dayjs(record.jobDateLocal).format('DD/MM/YYYY HH:mm a') : '';
          }
        },
        {
          id: 'requested_delivery_date',
          field: 'requestedDeliveryDate',
          text: 'Requested Delivery Date',
          autoWidth: 1,
          type: 'date',
          format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          editor: false,
        },
        {
          id: 'full_leg_completed_date',
          field: 'fullLegCompletedDate',
          text: 'Full MVT Completed Date',
          autoWidth: 1,
          type: 'date',
          format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.fullLegCompletedDate && record.fullLegCompletedDate.toString() !== 'Invalid Date' ? dayjs(record.fullLegCompletedDate).format('DD/MM/YYYY HH:mm a') : '';
          }
        },
        {
          id: 'dehire_leg_completed_date',
          field: 'dehireLegCompletedDate',
          text: 'Dehire MVT Completed Date',
          autoWidth: 1,
          type: 'date',
          format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.dehireLegCompletedDate && record.dehireLegCompletedDate.toString() !== 'Invalid Date' ? dayjs(record.dehireLegCompletedDate).format('DD/MM/YYYY HH:mm a') : '';
          }
        },
        {
          id: "pod",
          text: 'POD',
          field: 'containPOD',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'container_status_container_item',
          field: "containerStatusCode",
          text: 'Container Status',
          align: "center",
          autoWidth: 1,
          htmlEncode: false,
          tooltipRenderer: ({ record, cellTooltip, cellElement }: { record: Partial<ContainerJobGridStatusMovement>, cellTooltip: CellTooltip, cellElement: HTMLElement }) => {
            return new Promise(resolve => {
              this.containerService.getContainerPortStatus(record.containerJobItemId!)
                .subscribe({
                  next: (result) => {
                    const containerDisplayStatus: ContainerDisplayStatus = {
                      portStatus: { ...result },
                      transitStatus: null
                    }
                    resolve(ContainerStatusColumnConfig.toolTip(containerDisplayStatus, record.jobDirection!));
                  },
                  error: (errorResponse: HttpErrorResponse) => {
                    resolve(errorResponse.error);
                  }
                });
            })
          },
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            if (isExport) {
              return record.containerStatus?.transitStatus ?
                record.containerStatus?.transitStatus.code :
                record.containerStatus?.portStatus ?
                  record.containerStatus?.portStatus.status :
                  ''
            }

            if (record && record.containerStatus) {
              return ContainerStatusColumnConfig.content(record.containerStatus);
            } else {
              return "";
            }
          },
        },
        { id: 'container_job_number_container_item', field: 'containerJobNumber', text: 'Job Number', autoWidth: 1 },
        { id: 'order_number_container_item', field: 'orderNumber', text: 'Order Number', autoWidth: 1 },
        { id: 'customer_reference', field: 'customerReference', text: 'Customer Reference', autoWidth: 1 },
        { id: 'container_number', field: 'containerNumber', text: 'Container Number', autoWidth: 1 },
        { id: 'job_type', field: 'jobType', text: 'Job Type', autoWidth: 1, ...this.getFilterableCombo('jobType') },
        {
          id: 'job_status', field: 'jobItemStatus', text: 'Job Status', autoWidth: 1, ...this.getFilterableCombo('jobItemStatus'),
          htmlEncode: false,
          renderer: ({ record, isExport }: { record: any, isExport: boolean }) => {
            if (isExport) {
              return record.jobItemStatus;
            } else {
              switch (record.jobItemStatus) {
                case "Active":
                  return `<span class="status vertical-center" style="background-color:#ea2088; color: white;">${record.jobItemStatus}</span>`;
                case "Completed":
                  return `<span class="status vertical-center" style="background-color:#62cb31; color: white;">${record.jobItemStatus}</span>`;
                case "Cancelled":
                  return `<span class="status vertical-center" style="background-color:#e43321; color: white;">${record.jobItemStatus}</span>`;
                case "Billed":
                  return `<span class="status vertical-center" style="background-color:#ffb606; color: white;">${record.jobItemStatus}</span>`;
                case "Subcontractor":
                  return `<span class="status vertical-center" style="background-color:#ffb606; color: white;">${record.jobItemStatus}</span>`;
                default:
                  return `<span class="status vertical-center">${record.jobItemStatus}</span>`;
              }
            }
          }
        },
        { id: 'container_size', field: 'containerSize', text: 'Size', autoWidth: 1 },
        { id: 'container_type', field: 'containerType', text: 'Type', autoWidth: 1 },
        { id: 'weight', field: 'weight', text: 'Weight', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: 'gridNumber', field: 'gridNumber', text: 'Grid #', autoWidth: 1 },
        { id: 'door_direction', field: 'doorDirection', text: 'Door Direction', autoWidth: 1, ...this.getFilterableCombo('doorDirection') },
        {
          id: 'dangerous_goods',
          field: 'dangerousGoods',
          text: 'Dangerous Goods',
          autoWidth: 1,
          type: 'template',
          align: 'center',

        template: () => '',
        renderer: ({ value, isExport }: { value: boolean, isExport: boolean }) => {
          if (!isExport || value === null || value === undefined) {
            return '';
          }
          return value ? 'Yes' : 'No';
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: false,
            valueField: 'value',
            displayField: 'text',
            items: [
              { "text": "YES", "value": true },
              { "text": "NO", "value": false },
            ]
          }
        }
        },
        { id: 'dg_code', field: 'dgCode', text: 'DG Code', autoWidth: 1 },
        { id: 'ecn', field: 'ecn', text: 'ECN', autoWidth: 1 },
        { id: 'vessel_name', field: 'vesselName', text: 'Vessel Name', autoWidth: 1 },
        { id: 'vessel_eta_and_etd_date', field: 'vesselEtaAndEtdDate', text: 'Vessel ETA/D', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'vessel_eta_and_etd_time', field: 'vesselEtaAndEtdTime', text: 'Vessel ETA/D Time', autoWidth: 1, ...getTimeColumnGenericConfig() },
        { id: 'original_port', field: 'originalPort', text: 'Original Port', autoWidth: 1, ...this.getFilterableCombo('originalPort') },
        { id: 'delivery_point_name', field: 'deliveryPointName', text: 'Delivery Point Name', autoWidth: 1 },
        { id: 'pick_up_point_name', field: 'pickupPointName', text: 'Pickup Point Name', autoWidth: 1 },
        { id: 'demurrage_date', field: 'demurrageDate', text: 'Demurrage Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'demurrage_time', field: 'demurrageTime', text: 'Demurrage Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'demurrage_status', field: 'demurrageStatus', text: 'Demurrage Status', autoWidth: 1 },
        { id: 'detention_date', field: 'detentionDate', text: 'Detention Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'detention_time', field: 'detentionTime', text: 'Detention Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'receival_commence_date', field: 'receivalCommenceDate', text: 'Receival Commencement Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'receival_commence_time', field: 'receivalCommenceTime', text: 'Receival Commencement Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'receival_cutoff_date', field: 'receivalCutoffDate', text: 'Receival Cut-off Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'receival_cutoff_time', field: 'receivalCutoffTime', text: 'Receival Cut-off Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        {
          id: 'full_leg_only',
          field: 'fullLegOnly',
          text: 'Full Leg Only',
          autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'execution_date', field: 'executionDate', text: 'Execution Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.executionDate && record.executionDate.toString() !== 'Invalid Date' ? dayjs(record.executionDate).format('DD/MM/YYYY') : '';
          }
        },
        { id: 'job_shift', field: 'jobShift', text: 'Job Shift', autoWidth: 1 },
        { id: 'live_unload', field: 'liveUnload', text: 'Live Unload', autoWidth: 1 },
        {
          id: 'mpi_inspection_required',
          field: 'mpiInspectionRequired',
          text: 'MPI Inspection Required',
          autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        { id: 'delivery_point_address', field: 'deliveryPointAddress', text: 'Delivery Point Address ', autoWidth: 1 },
        { id: 'pickup_point_address', field: 'pickupPointAddress', text: 'Pickup Point Address', autoWidth: 1 },
        { id: 'devan_date', field: 'devanDate', text: 'Planned Devan Date ', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'mt_transfer_point', field: 'mtTransferPoint', text: 'MT Transfer Point', autoWidth: 1 },
        { id: 'acceptance_number', field: 'acceptanceNumber', text: 'Acceptance Number', autoWidth: 1 },
        { id: 'release_code', field: 'releaseCode', text: 'Release Code', autoWidth: 1 },
        { id: 'train_wagon_number', field: 'trainWagonNumber', text: 'Train Wagon Number', autoWidth: 1 },
        {
          id: 'acceptance_is_active',
          field: 'acceptanceIsActive',
          text: 'Acceptance Active',
          autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'release_is_active',
          field: 'releaseIsActive',
          text: 'Release Active',
          autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'docs_received_late', field: 'docsReceivedLate', text: 'Docs Received Late', autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        { id: 'devan_type', field: 'devanType', text: 'Planned Devan Type', autoWidth: 1 },
        { id: 'outbound_vessel_name', field: 'outboundVesselName', text: 'Outbound Vessel Name', autoWidth: 1 },
        {
          id: 'needs_lift',
          field: 'needsLift',
          text: 'Needs Lift',
          autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'is_high_gauge',
          field: 'isHighGauge',
          text: 'High Gauge',
          autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'is_wide_gauge',
          field: 'isWideGauge',
          text: 'Wide Gauge',
          autoWidth: 1,
          type: 'template',
          align: 'center',
        ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'dwell_time_in_days',
          field: 'currentDwellTimeDays',
          text: 'Dwell Time (Days)',
          autoWidth: 1,
        },
        { id: 'container_shipping_line', field: 'containerOperatorName', text: 'Shipping Line', autoWidth: 1 },
        { id: 'container_discharge_date', field: 'dischargedDate', text: 'Discharged Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'container_discharge_time', field: 'dischargedTime', text: 'Discharged Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'temperature', field: 'temperature', text: 'Temperature', autoWidth: 1, ...getNumberColumnGenericConfig() },
        {
          id: 'ready_for_pickup_date', field: 'readyForPickupDate', text: 'RFP Date Time', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm a', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.readyForPickupDate && record.readyForPickupDate.toString() !== 'Invalid Date' ? dayjs(record.readyForPickupDate).format('DD/MM/YYYY HH:mm a') : '';
          }
        },
        { id: 'container_location_name', field: 'containerLocationName', text: 'Location', autoWidth: 1 },
        {
          id: 'container_available_date', field: 'containerAvailableDateTime', text: 'Available Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm a', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.containerAvailableDateTime && record.containerAvailableDateTime.toString() !== 'Invalid Date' ? dayjs(record.containerAvailableDateTime).format('DD/MM/YYYY HH:mm a') : '';
          }
        },
        {
          id: 'planned_pak_devan_date', field: 'containerHandlingDateUtc', text: 'Planned PAK/Devan Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.containerHandlingDateUtc && record.containerHandlingDateUtc.toString() !== 'Invalid Date' ? dayjs(record.containerHandlingDateUtc).format('DD/MM/YYYY') : '';
          }
        },
        { id: 'seal_number', field: 'sealNumber', text: 'Seal Number', autoWidth: 1 },
      ];

      if (userType && userType === UserType.Subcontractor) {
        this.gridHeight = '74vh';
        columns.push({ id: 'subcontractor_instructions', field: 'subcontractorInstructions', text: 'Instructions', autoWidth: 1 });
      } else {
        columns.splice(3, 0, { id: 'freight_payer_name', field: 'freightPayerName', text: 'Freight Payer', autoWidth: 1, ...this.getFilterableCombo('freightPayerName') },)
      }

      const excelFormats = new Map(columns.map(column => [column.field, column.excelFormat]));

      this.gridProps = {
        stateful: true,
        columns: [
          {
            id: 'action',
            width: '10em',
            text: 'Action',
            field: 'action',
            hidden: false,
            align: 'center',
            type: 'widget',
            hideable: false,
            draggable: false,
            htmlEncode: false,
            filterable: false,
            headerRenderer: ({ record }: any) => {
              return `<label>Action</label><br><button class="btn-xs btn btn-primary clearfilter">Clear Filters</button> `;
            },
            widgets: [
              {
                type: 'button',
                text: 'Options',
                menuIcon: undefined,
                onAction: ({ source: btn }: any) => {
                  let menuItems: Partial<MenuItemConfig>[] = [
                    { text: 'View Job' },
                    { text: 'Open POD', hidden: !btn.cellInfo.record.fullLegCompletedDate },
                    { text: 'Print POD', hidden: !btn.cellInfo.record.fullLegCompletedDate },
                  ];
                  if (menuItems.length > 0) {
                    const menu = (btn.menu || (btn.menu = new Menu({
                      forElement: btn.element,
                      items: menuItems,
                      onItem: ({ item }) => {
                        if (item.text === 'View Job') {
                          this.openContainerJobEyeView(btn.cellInfo.record.containerJobItemId, btn.cellInfo.record.containerLegId);
                        }
                        if (item.text === 'Open POD') {
                          this.openContainerJobPreviewPodModal(btn.cellInfo.record.containerJobItemId);
                        }
                        if (item.text === 'Print POD') {
                          this.printContainerJobPodPdf(btn.cellInfo.record.containerJobItemId);
                        }
                      }
                    }))) as Menu;
                    menu.show();
                  }
                }
              }
            ],
          },
          ...columns
        ],
        rowCopyPasteFeature: false,
        cellMenuFeature: false,
        filterBarFeature: true,
        cellEditFeature: true,
        cellTooltipFeature: {
          textContent: true,
          hoverDelay: 500,
          tooltipRenderer: ({ record, column }: { record: any, column: ColumnConfig }) => {
            if (column.field === 'currentDwellTimeDays') {
              return StringHelper.encodeHtml('Displays the current dwell duration for active containers or final dwell time for completed containers.');
            }

            return StringHelper.encodeHtml('');
          }
        },
        excelExporterFeature: {
          zipcelx: ({ filename, rows, columns: exportedColumns }: { filename: string, rows: { value: string | number | Date | null | undefined }[][], columns: { field: string, value: string }[] }) => {
            const data = rows.map(row => row.map(({ value }, index): Cell => {
              if (value === null || value === undefined || value === '') {
                return null;
              }
              const format = excelFormats.get(exportedColumns[index].field);
              if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? null : { value, type: Date, format: format as string };
              }
              if (typeof value === 'number') {
                return { value, type: Number, format: typeof format === 'function' ? format(value) : format };
              }
              return { value, type: String };
            }));
            return writeXlsxFile([
              exportedColumns.map(column => ({ value: column.value, type: String, fontWeight: 'bold' } as Cell)),
              ...data
            ]).toFile(`${filename}.xlsx`);
          }
        },
        store: {
          listeners: {
            change: () => this.updateTotalItemsFromStore(),
            filter: () => this.updateTotalItemsFromStore(),
          }
        },
        listeners: {
          headerClick: (event: any) => this.headerClick(event),
          beforeCellEditStart: ({ editorContext }) => {
            return editorContext.column.field === 'pin'
              || editorContext.column.field === 'customerReference';
          },
          finishCellEdit: ({ editorContext }) => {
            if (editorContext.column.field === 'pin' || editorContext.column.field === 'customerReference') {
              const request = {
                containerJobItemId: editorContext.record.get('containerJobItemId'),
                fieldName: editorContext.column.field,
                value: editorContext.value
              };
              this.containerService.patchContainerJobItem(request).subscribe({
                next: (response) => {
                  this.toasterService.showDefault("The selected container(s) has been successfully updated!")
                },
                error: (errorResponse: HttpErrorResponse) => {
                  this.toasterService.showDanger("Failed to update the job");
                }
              })
            }
          }
        },
        selectionMode: {
          checkbox: true,
          showCheckAll: true,
          row: false,
          cell: false
        },
      };

      this.isInitializing = false;
    });
  }

  onCellClick({ record, column }: { record: any, column: ColumnConfig }) {

  }

  headerClick(event: any) {
    if (event && event.domEvent && event.domEvent.target && event.domEvent.target.classList.contains('clearfilter')) {
      if (this.containerGrid && this.containerGrid.instance) {
        (this.containerGrid.instance.store as any).clearFilters();
      }
    }
  }

  toggleAllGroupings(action: 'expand' | 'collapse') {
    if (this.containerGrid && this.containerGrid.instance) {
      if (action === 'collapse') {
        this.containerGrid.instance.collapseAll();
      }

      if (action === 'expand') {
        this.containerGrid.instance.expandAll();
      }
    }
  }

  exportToCsv() {
    if (!this.containerGrid || !this.containerGrid.instance) {
      return;
    }

    const grid = this.containerGrid.instance;
    const columns = (grid.columns as ColumnStore).visibleColumns.filter(column =>
      column.get('exportable') !== false && column.field && column.field !== 'action');
    const date = dayjs().format('YYYY-MM-DD-HH-mm');
    const exportConfig = {
      filename: `trifecta-container-${date}`,
      dateFormat: '',
      exporterConfig: { columns }
    };
    return grid.features.excelExporter.export(exportConfig);
  }

  loadJobsList(dateRange: IContainerSearchState['searchParams']) {
      if (this.isLoadingJobs) {
          this.pendingSearchDateRange = dateRange;
          this.syncGridMask();
          return;
      }

      this.pendingSearchDateRange = null;
      this.isLoadingJobs = true;
      this.syncGridMask();

      this.containerService.getContainerJobs(dateRange.from, dateRange.to, dateRange.searchDateOption).pipe(take(1)).subscribe({
          next: (jobs) => {
              if (this.loadPendingJobs()) {
                  return;
              }

              this.gridSelected = [];
              this.selectedTotalItems = 0;
              const refreshedJobs = jobs.map(x => {
                  const isFullLegCompleted = (x.inboundFullLegCompletedDateUtc || x.outboundFullLegCompletedDateUtc) ? true : false;
                  const fullLegCompletedDate = x.inboundFullLegCompletedDateUtc ?? x.outboundFullLegCompletedDateUtc;

                  return {
                      ...x,
                    jobDateLocal: x.jobDateLocal ? new Date(x.jobDateLocal) : null,
                    requestedDeliveryDate: x.requestedDeliveryDate ? new Date(x.requestedDeliveryDate) : null,
                    fullLegCompletedDate: fullLegCompletedDate ? DateTimeHelper.utcToLocal(fullLegCompletedDate) : null,
                    dehireLegCompletedDate: x.dehireLegCompletedDate ? DateTimeHelper.utcToLocal(x.dehireLegCompletedDate) : null,
                    devanDate: x.devanDate ? new Date(x.devanDate) : null,
                    demurrageDate: DateTimeHelper.parseBryntumDate(x.demurrageDate),
                    demurrageTime: DateTimeHelper.parseBryntumTime(x.demurrageTime),
                    detentionDate: DateTimeHelper.parseBryntumDate(x.detentionDate),
                    detentionTime: DateTimeHelper.parseBryntumTime(x.detentionTime),
                    receivalCommenceDate: DateTimeHelper.parseBryntumDate(x.receivalCommenceDate),
                    receivalCommenceTime: DateTimeHelper.parseBryntumTime(x.receivalCommenceTime),
                    receivalCutoffDate: DateTimeHelper.parseBryntumDate(x.receivalCutoffDate),
                    receivalCutoffTime: DateTimeHelper.parseBryntumTime(x.receivalCutoffTime),
                    firstNotifiedDate: x.firstNotifiedDate ? new Date(x.firstNotifiedDate) : null,
                    docsReceivedDate: x.docsReceivedDate ? new Date(x.docsReceivedDate) : null,
                    containPOD: isFullLegCompleted,
                    readyForPickupDate: x.readyForPickupDateUtc ? DateTimeHelper.utcToLocal(x.readyForPickupDateUtc) : null,
                    containerAvailableDateTime: x.containerAvailableDateTime ? new Date(x.containerAvailableDateTime) : null,
                    executionDate: DateTimeHelper.parseBryntumDate(x.executionDate),
                    containerHandlingDateUtc: x.containerHandlingDateUtc ? DateTimeHelper.utcToLocal(x.containerHandlingDateUtc) : null,
                    vesselEtaAndEtdDate: DateTimeHelper.parseBryntumDate(x.vesselEtaAndEtdDate),
                    dischargedDate: DateTimeHelper.parseBryntumDate(x.dischargedDate),
                    dischargedTime: DateTimeHelper.parseBryntumTime(x.dischargedTime),
                    containerLocationName: x.containerLocationType && this.containerLocationTypeThatDisplayEmptyPark.includes(x.containerLocationType) ? "Empty Park" : x.containerLocationName
                  }
              }) as ContainerJobGridStatusMovement[];
              this.setGridData(refreshedJobs);
              this.totalItems = this.data.length;
              queueMicrotask(() => this.updateTotalItemsFromStore());
              this.finishLoadingJobs();
          },
          error: (err) => {
            if (this.loadPendingJobs()) {
              return;
            }

            this.finishLoadingJobs();
            if (err.status === 401) {
              this.toasterService.showDanger("Refresh token failed. Please log out and log back in.");
            } else {
              this.toasterService.showDanger("Failed to load jobs!", true);
            }
          }
      });
  }

  private loadPendingJobs(): boolean {
    const pendingSearchDateRange = this.pendingSearchDateRange;
    if (!pendingSearchDateRange) {
      return false;
    }

    this.pendingSearchDateRange = null;
    this.isLoadingJobs = false;
    this.loadJobsList(pendingSearchDateRange);
    return true;
  }

  private finishLoadingJobs(): void {
    this.isLoadingJobs = false;
    this.syncGridMask();
  }

  onGridSelectionChange(event: any) {
    this.gridSelected = (event.selection ?? []) as ContainerJobGridStatusMovement[];
    this.selectedTotalItems = this.gridSelected.length;
    this.statusFlags = ContainerStatusHelper.determineContainerStatusFlag(this.gridSelected);
  }

  async updateContainerStatus() {
    const containerJobItems = this.gridSelected;
    const containerJobItemIds = Array.from(
      new Set(containerJobItems.map((record: { containerJobItemId: any; }) => record.containerJobItemId))
    );

    if (this.statusFlags.none) {
      const modalRef = this.modalService.open(ContainerChangeStatusModalComponent, { size: 'xl' });
      modalRef.componentInstance!.containerJobItems = containerJobItems;

      try {
        const result = await modalRef.result;
        if (result) {
          this.containerGrid?.instance.deselectAll();
          this.loadJobsList(this.searchDateRange!);
        }
      } catch (dismissReason) {
        return;
      }

    } else {
      const statusList = Object.values(ContainerStatusConstant);
      let newStatusId: number = 0;
      if (this.statusFlags.isReadyForPickup) {
        newStatusId = ContainerStatusConstant.ReadyForPickup.id;
      } else if (this.statusFlags.isUnpack) {
        newStatusId = ContainerStatusConstant.Unpack.id;
      } else if (this.statusFlags.isPack) {
        newStatusId = ContainerStatusConstant.Pack.id;
      }
      if (newStatusId === 0) {
        return;
      }

      const newStatus = statusList.find(status => status.id === newStatusId);

      let resultObservable$: Observable<boolean> | null = new Observable<boolean>();

      if (this.statusFlags.isUnpack && !this.statusFlags.isReadyForPickup && !this.statusFlags.isPack && !this.statusFlags.none) {
        const containerLegId = containerJobItems[0].containerLegId;
        resultObservable$ = this.containerService.getCanContainerBeSetBackToUnpack(containerLegId);
      } else {
        resultObservable$ = new Observable<boolean>(subscriber => {
          subscriber.next(true);
        });
      }
      resultObservable$.subscribe({
        next: (result) => {
          if (result) {
            this.showConfirmStatusChangeModal(newStatus, containerJobItemIds);
          } else {
            this.showCantUnpackModal();
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.toasterService.showDanger(errorResponse.error);
        }
      });
    }
  }

  async updateContainers() {
    const containerJobItems = this.gridSelected;

    if (containerJobItems.length === 0) {
      return;
    }

    if (containerJobItems.length > this.maxContainerUpdateItems) {
      this.toasterService.showDanger(`Only ${this.maxContainerUpdateItems} jobs can be updated at a time.`);
      return;
    }

    const nonEditableContainerJobNumbers = this.getNonEditableContainerJobNumbers(containerJobItems);
    if (nonEditableContainerJobNumbers.length > 0) {
      this.toasterService.showDanger(`Completed, billed or cancelled jobs cannot be updated: ${nonEditableContainerJobNumbers.join(', ')}.`);
      return;
    }

    const updateRequest = await this.openContainersUpdateModal(containerJobItems);
    if (!updateRequest) {
      return;
    }

    this.containerService.updateContainers(updateRequest).subscribe({
      next: () => {
        this.toasterService.showDefault("The selected container(s) has been successfully updated!");
        this.refreshJobsList();
      },
      error: (errorResponse: HttpErrorResponse) => {
        let errorMessage = "Failed to update the selected container(s).";
        if (typeof errorResponse.error === 'string' && errorResponse.error.length > 0) {
          errorMessage = errorResponse.error;
        }

        this.toasterService.showDanger(errorMessage);
      }
    });
  }

  private refreshJobsList() {
    this.containerGrid?.instance.deselectAll();
    this.gridSelected = [];
    this.selectedTotalItems = 0;
    this.statusFlags = ContainerStatusHelper.determineContainerStatusFlag(this.gridSelected);

    const gridStore = this.containerGrid?.instance?.store as any;
    if (gridStore) {
      gridStore.removeAll();
    }

    this.data = [];
    this.totalItems = 0;

    if (this.searchDateRange) {
      this.loadJobsList(this.searchDateRange);
    }
  }

  private setGridData(jobs: ContainerJobGridStatusMovement[]) {
    this.data = jobs;

    const gridStore = this.containerGrid?.instance?.store as any;
    if (!gridStore) {
      return;
    }

    gridStore.data = jobs;
  }

  private getNonEditableContainerJobNumbers(containerJobItems: ContainerMovementListItem[]): string[] {
    const jobNumbers = containerJobItems
      .filter(containerJobItem => !this.canUpdateContainerJobItem(containerJobItem))
      .map(containerJobItem => containerJobItem.containerJobNumber);

    return Array.from(new Set(jobNumbers));
  }

  private canUpdateContainerJobItem(containerJobItem: ContainerMovementListItem): boolean {
    return containerJobItem.jobItemStatusId !== ContainerJobItemStatusEnum.Cancelled
      && containerJobItem.jobItemStatusId !== ContainerJobItemStatusEnum.Completed
      && containerJobItem.jobItemStatusId !== ContainerJobItemStatusEnum.Billed;
  }

  async showCantUnpackModal() {
    const confirmed = await this.confirmationService.confirm(
      'Error',
      `Unable to change the status because the dehire leg has already been planned. Please contact the carrier for assistance.`,
      'Okay',
      '',
      false,
      'danger'
    );

    return confirmed;
  }

  async showConfirmStatusChangeModal(status: any, containerJobItemIds: number[]) {
    const confirmed = await this.confirmationService.confirm(
      'Update Container Status',
      `Change container status to ${status.name} for selected container(s)?`,
      'Update',
      'Cancel',
    );

    if (!confirmed) {
      return;
    }

    let model: ContainerUpdateStatusRequest[] = [{
      containerJobItemIds: containerJobItemIds,
      containerStatus: { ...status, containerStatusId: status.id },
      isHeld: false,
      legTypes: []
    }];

    if (model) {
      this.containerService.updateContainerJobItemStatus(model).subscribe({
        next: (response) => {
          this.toasterService.showDefault("The selected container(s) has been successfully updated!")
          this.loadJobsList(this.searchDateRange!);
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.toasterService.showDanger(errorResponse.error);
        }
      });
    }
  }

  getContainerStatusActionLabel(): string {
    if (this.gridSelected.length > 0) {

      if (this.statusFlags.isReadyForPickup) {
        return "Ready For Pickup"
      }

      if (this.statusFlags.isPack) {
        return "Pack"
      }

      if (this.statusFlags.isUnpack) {
        return "Unpack"
      }
    }

    return "Change Container Status"
  }

  openContainerJobPreviewPodModal(containerJobItemId: number) {
      const modalRef = this.modalService.open(ContainerJobPreviewPodModalComponent, { size: 'xl' });
      modalRef.componentInstance.containerJobItemId = containerJobItemId;
  }

  openContainerJobEyeView(containerJobItemId: number, containerLegId: number) {
    const modalRef = this.modalService.open(ContainerJobEyeViewModalComponent, { size: 'xl' });
    modalRef.componentInstance.containerJobItemId = containerJobItemId;
    modalRef.componentInstance.containerLegId = containerLegId;
  }

  async openContainersUpdateModal(containerMovementLists: ContainerMovementListItem[]): Promise<ContainerUpdateRequest | null> {
    const modalRef = this.modalService.open(ContainerUpdateModalComponent, { size: 'xl', windowClass: 'container-update-modal-xl' });
    modalRef.componentInstance.containerMovementLists = containerMovementLists;

    try {
      return await modalRef.result as ContainerUpdateRequest;
    } catch (dismissReason) {
      return null;
    }
  }

  printContainerJobPodPdf(containerJobItemId: number) {
      this.proofOfDeliveryPdfService.printContainerJobPodPdf(containerJobItemId).subscribe({
        next: (response) => {
          if (response) {
            this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
          }
        },
        error: (response: HttpErrorResponse) => {
          console.error(response.message, 'An error occurred:');
        }
      });
    }

    createRedirect() {
        this.router.navigate(['app/containers/create']);
    }

  getFilterableCombo(field: string) {
    return {
      filterable: {
        filterField: {
          type: 'combo',
          multiSelect: true,
          valueField: field,
          displayField: field
        }
      }
    }
  }

  private syncGridMask() {
    if (!this.containerGrid?.instance || this.containerGrid.instance.isDestroyed) {
      return;
    }

    if (this.isLoadingJobs) {
      this.containerGrid.instance.mask('Loading...');
      return;
    }

    this.containerGrid.instance.unmask();
  }

  private updateTotalItemsFromStore() {
    const gridStore = this.containerGrid?.instance?.store as any;
    if (!gridStore) {
      this.totalItems = this.data.length;
      return;
    }

    const allRecords: any[] = gridStore.allRecords as any[];
    if (!allRecords || allRecords.length === 0) {
      this.totalItems = 0;
      return;
    }

    const filterFn = gridStore.filtersFunction;
    const hasActiveFilterFn = typeof filterFn === 'function';

    let count = 0;
    for (let i = 0; i < allRecords.length; i++) {
      const record = allRecords[i];
      if (record && !record.isSpecialRow && (!hasActiveFilterFn || filterFn(record))) {
        count++;
      }
    }

    this.totalItems = count;
  }
}
